import { StationGroupPermissionItem } from '@app/modules/admin/components/stations-permissions/StationGroupPermissionItem';
import { GroupPermissionState } from '@app/modules/admin/types';
import { mapPromises } from '@mi-front/core';
import { AsyncView, NullState, SearchBox } from '@mi-front/ui';
import { Grid } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStation } from '../../../../../core/stations/hooks/useStation';
import { fetchGroupsPermissions } from '../../../api/permissions';
import { NoGroups } from '../../../components/NoGroups';

interface Props {
  readonly data: {
    readonly permissionsState: readonly GroupPermissionState[];
  };
  readonly stationId: string;
}

export function AdminStationsGroupsPermissionsView(p: Props) {
  const { data, stationId } = p;
  const { permissionsState } = data;
  const { $t } = useIntl();
  const [filter, setFilter] = useState('');
  const [internalState, setInternalState] = useState(permissionsState);

  const filteredPermissions = useMemo(() => {
    if (!filter) {
      return internalState;
    }

    return internalState.filter((pState) => {
      return !!pState.group.name.match(new RegExp(filter, 'i'));
    });
  }, [internalState, filter]);

  const handleUpdate = (oldState: GroupPermissionState, updatedState: GroupPermissionState) => {
    const idx = internalState.indexOf(oldState);
    if (idx !== -1) {
      setInternalState([
        ...internalState.slice(0, idx),
        updatedState,
        ...internalState.slice(idx + 1),
      ]);
    }
  };

  if (!permissionsState.length) {
    return <NoGroups />;
  }

  if (filteredPermissions.length === 0) {
    return (
      <div data-cy="permissions-view">
        <Grid container sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <SearchBox
              data-cy="permissions-filter"
              onSearch={setFilter}
              placeholder={$t({
                id: 'admin.stations.viewScreen.groupsFilter',
                defaultMessage: 'Filtrar grupos',
              })}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        <NullState>
          <FormattedMessage
            id="admin.stations.viewScreen.noGroupsFound"
            defaultMessage="No se encontraron grupos coincidentes"
          />
        </NullState>
      </div>
    );
  }

  return (
    <div data-cy="permissions-view">
      <Grid container sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <SearchBox
            data-cy="permissions-filter"
            onSearch={setFilter}
            placeholder={$t({
              id: 'admin.stations.viewScreen.groupsFilter',
              defaultMessage: 'Filtrar grupos',
            })}
            sx={{ mb: 2 }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {filteredPermissions.map((permissionState) => (
          <Grid key={permissionState.group.id} item xs={12} md={6} lg={4}>
            <StationGroupPermissionItem
              permissionState={permissionState}
              stationId={stationId}
              onUpdate={(updated) => {
                handleUpdate(permissionState, updated);
              }}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

export const ConnAdminStationsGroupsPermissionsView = (p: { slug: string }) => {
  const station = useStation(p.slug);

  return (
    <AsyncView
      promiseFn={() =>
        mapPromises({
          permissionsState: fetchGroupsPermissions(station.id),
        })
      }
    >
      {(pp) => <AdminStationsGroupsPermissionsView {...pp} stationId={station.id} />}
    </AsyncView>
  );
};
